import { useEffect, useState, useRef } from 'react';

/**
 * useAudioLevel - Custom hook for real-time audio level detection
 * Uses Web Audio API to analyze microphone input amplitude
 * 
 * @returns {Object} - { audioLevel, startListening, stopListening, isListening }
 */
const useAudioLevel = () => {
  const [audioLevel, setAudioLevel] = useState(0);
  const [isListening, setIsListening] = useState(false);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const microphoneRef = useRef(null);
  const animationFrameRef = useRef(null);
  const lastUpdateTimeRef = useRef(0); // ✅ CRITICAL FIX: Throttle setState

  // Analyze audio amplitude
  const analyzeAudio = () => {
    if (!analyserRef.current) return;

    const analyser = analyserRef.current;
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const checkLevel = () => {
      analyser.getByteFrequencyData(dataArray);

      // Calculate average amplitude
      const sum = dataArray.reduce((acc, value) => acc + value, 0);
      const average = sum / bufferLength;
      const normalizedLevel = Math.min(average / 128, 1); // Normalize to 0-1

      // ✅ CRITICAL FIX: Throttle setState to max 10 updates/sec (100ms) instead of 60/sec
      const now = Date.now();
      if (now - lastUpdateTimeRef.current >= 100) {
        setAudioLevel(normalizedLevel);
        lastUpdateTimeRef.current = now;
      }

      animationFrameRef.current = requestAnimationFrame(checkLevel);
    };

    checkLevel();
  };

  // Start listening to microphone
  const startListening = async () => {
    try {
      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        } 
      });

      // Create audio context
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;
      analyserRef.current.smoothingTimeConstant = 0.8;

      // Connect microphone to analyser
      microphoneRef.current = audioContextRef.current.createMediaStreamSource(stream);
      microphoneRef.current.connect(analyserRef.current);

      setIsListening(true);
      analyzeAudio();

      console.log('✅ Audio level detection started');
    } catch (error) {
      console.error('❌ Error accessing microphone:', error);
      setIsListening(false);
    }
  };

  // Stop listening to microphone
  const stopListening = () => {
    // Cancel animation frame
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    // Stop microphone stream
    if (microphoneRef.current && microphoneRef.current.mediaStream) {
      microphoneRef.current.mediaStream.getTracks().forEach(track => track.stop());
    }

    // Close audio context
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    analyserRef.current = null;
    microphoneRef.current = null;
    setAudioLevel(0);
    setIsListening(false);

    console.log('✅ Audio level detection stopped');
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopListening();
    };
  }, []);

  return {
    audioLevel,
    startListening,
    stopListening,
    isListening
  };
};

export default useAudioLevel;
